import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames/dedupe';
import some from 'lodash/some';

import PulsingCircle from './PulsingCircle';
import ErrorState from './ErrorState';
import BlankState from './BlankState';
import Pagination from './Pagination';
import Tr from './Tr';
import THead from './THead';
import FilterSelector from './FilterSelector';
import SearchInput from './SearchInput';

import './stylesheets/SimpleTable.scss';


class SimpleTable extends React.PureComponent {

  renderExceptionStates() {
    const { loading, errored, empty, noMatches, retry, dogfoodIdentifier } = this.props;
    const {
      emptyState = <BlankState title="No records" />,
      noMatchesState = <BlankState title="No matching records" />
    } = this.props;

    let exceptionState;

    if (errored) {
      const errorStateProps = { retry, dogfoodIdentifier, componentName: 'SimpleTable' };
      exceptionState = <ErrorState {...errorStateProps} />;
    } else if (loading) {
      exceptionState = <PulsingCircle />;
    } else if (empty) {
      exceptionState = emptyState;
    } else if (noMatches) {
      exceptionState = noMatchesState;
    }

    if (exceptionState) {
      return <div className="table-exception-states">{ exceptionState }</div>;
    }
  }

  render() {
    const { data, type, columns, className, limit, loading, errored, empty, noMatches } = this.props;
    const { tableActionMenu } = this.props;

    const { sort, onSort } = this.props;                                // Sorting Props
    const { filters, selectedFilters, onFilter } = this.props;          // Filtering Props
    const { totalCount, onPageChange, perPage, offset } = this.props;   // Pagination Props
    const { onSearch, searchTerm } = this.props;                        // Search Props

    const classes = {
      loc: true,
      table: true,
      'default-table': type === 'default',
      'card-table': type === 'card'
    };

    const searchable = some(columns, 'searchable');
    const visibleColumns = columns.filter(c => !c.hidden);
    const displayData = (!loading && !errored && !empty && !noMatches);

    const filterSelectorProps = { filters, selectedFilters, onFilter };
    const paginationProps = { totalCount, onPageChange, perPage, offset };

    const limitedData = limit ? data.slice(0, limit) : data;

    // FIXME: another key iterator
    /* eslint-disable */
    const renderRows = () => limitedData.map((row, index) =>
      <Tr disabled={row.disabled} key={index} columns={visibleColumns} data={row} onClick={row.onClick} />
    );
    /* eslint-enable */

    return (
      <div className={classNames(classes, className)}>
        <div className="table-controls">
          <div className="search-filters">
            { searchable ? <SearchInput onChange={onSearch} defaultSearchTerm={searchTerm} /> : null }
            { filters ? <FilterSelector {...filterSelectorProps} /> : null }
          </div>

          { tableActionMenu }
        </div>

        <div className="table-content">
          <table>
            <THead columns={visibleColumns} type={type} sort={sort} thOnClick={onSort} />
            <tbody>
              { displayData ? renderRows() : null }
            </tbody>
          </table>
        </div>

        { this.renderExceptionStates() }

        { (displayData && perPage) ? <Pagination {...paginationProps} /> : null }
      </div>
    );
  }
}

SimpleTable.propTypes = {
  /** Array of javascript objects with keys as column names */
  data: PropTypes.arrayOf(PropTypes.object).isRequired,
  /** The look and feel of the table depending on context. (e.g. default, card) */
  type: PropTypes.oneOf(['default', 'card']),
  /** List of columns to be displayed. Each object representing a column can have
    * the following properties: key, label, sortable, searchable, type, format and
    * component.
    */
  columns: PropTypes.arrayOf(PropTypes.oneOfType([
    PropTypes.shape({
      key: PropTypes.string,
      label: PropTypes.string,
      sortable: PropTypes.bool,
      searchable: PropTypes.bool,
      type: PropTypes.oneOf(['string', 'number', 'date']),
      format: PropTypes.func,
      component: PropTypes.func
    }),
    PropTypes.string
  ])).isRequired,
  /** Applied to the SimpleTable's root element */
  className: PropTypes.string,
  /** Max amount of elements to be displayed in the table */
  limit: PropTypes.number,
  /** It defines the ordering for the table. It receives the column name (or
    * function that take a row to determine column name) and direction (asc, desc)
    */
  sort: PropTypes.shape({
    column: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.func
    ]),
    direction: PropTypes.oneOf(['asc', 'desc'])
  }),
  /** Function called when clicking on a Table Header */
  onSort: PropTypes.func,
  /** Array of objects with filters and their values */
  filters: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string,
      label: PropTypes.string,
      values: PropTypes.arrayOf(
        PropTypes.shape({
          key: PropTypes.string,
          label: PropTypes.string
        })
      )
    })
  ),
  /** An object with the column names as keys and an array of { key, label }
    * objects as values
    */
  selectedFilters: PropTypes.object, // eslint-disable-line
  /** Function called with the new selectedFilters */
  onFilter: PropTypes.func,
  /** Total number of items in the collections use for Pagination */
  totalCount: PropTypes.number,
  /** Function called when the page changes */
  onPageChange: PropTypes.func,
  /** Number of elements perPage in the table */
  perPage: PropTypes.number,
  /** Indicates the element index in the collection to first be displayed */
  offset: PropTypes.number,
  /** Function called when the user enters a search term */
  onSearch: PropTypes.func,
  /** String to preload the search box with */
  searchTerm: PropTypes.string,
  /** Flag to indicates the table data is being loaded */
  loading: PropTypes.bool,
  /** Flag to indicate there was an error when loading data */
  errored: PropTypes.bool,
  /** Flag to indicate if the table is empty */
  empty: PropTypes.bool,
  /** Flag to indicate if all the results in the table are filtered out */
  noMatches: PropTypes.bool,
  /** Function that retries loading the campaigns */
  retry: PropTypes.func,
  /** Component to be inserted above the table in the right corner */
  tableActionMenu: PropTypes.object, // eslint-disable-line
  /** Blank state component to be used when the table has no data */
  emptyState: PropTypes.node,
  /** Blank state component to be used when the user performs a search and there
    * are no matching results
    */
  noMatchesState: PropTypes.node,
  /** Name used when dogfooding error states in the table, only provide it if
    * you want dogfooding.
    */
  dogfoodIdentifier: PropTypes.string
};

SimpleTable.defaultProps = {
  type: 'default',
  loading: false,
  errored: false
};

export default SimpleTable;



// WEBPACK FOOTER //
// ./node_modules/@localytics/loc-components/lib/SimpleTable.jsx